// SPDX-License-Identifier: AGPL-3.0-or-later

import {PurchaseDisabledWrapper} from '@app/features/app/components/dialogs/components/plutonium/PurchaseDisabledWrapper';
import {BoostGemIcon} from '@app/features/boost/components/BoostGemIcon';
import {BoostSubscriptionModal} from '@app/features/boost/components/BoostSubscriptionModal';
import styles from '@app/features/boost/components/GuildBoostBuyButton.module.css';
import {useGuildBoostAction} from '@app/features/boost/hooks/useGuildBoostAction';
import {useGuildBoostPurchase} from '@app/features/boost/hooks/useGuildBoostPurchase';
import BoostSubscription from '@app/features/boost/state/BoostSubscription';
import GuildBoosts from '@app/features/boost/state/GuildBoosts';
import {ShopCheckoutModal} from '@app/features/shop/components/modals/ShopCheckoutModal';
import {ShopPrice} from '@app/features/shop/components/ShopPrice';
import ShopCatalog from '@app/features/shop/state/ShopCatalog';
import {BOOST_LIMIT_DESCRIPTOR, BUY_BOOST_DESCRIPTOR} from '@app/features/shop/utils/ShopMessageDescriptors';
import {formatPayable, priceCurrency, shownCents} from '@app/features/shop/utils/ShopPriceFormat';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const SLOT_ARRIVED_DESCRIPTOR = msg({
	message: 'Your boost slot is here. You can boost this community now.',
	comment:
		'Toast when Buy a boost was pressed but a bought boost slot paid earlier has just arrived, so no new payment opens.',
});

// The shop's own checkout for boost:30; the price on the button is the catalog's, the dialog shows the server's quote.
// Where boosts sell as a monthly subscription (Paddle), the boost subscription dialog instead: a quantity, one payment.
export const GuildBoostBuyButton = observer(() => {
	const {i18n} = useLingui();
	const {handleBoost} = useGuildBoostAction();
	const {state, boost, limit} = useGuildBoostPurchase();
	const [checking, setChecking] = useState(false);
	if (state === 'hidden' || !boost) return null;
	const full = state === 'limit_reached';
	const monthly = Boolean(boost.recurring_quote);
	// A payment left waiting may have gone through since: the slots are read first, so a slot that came opens no second
	// payment.
	const open = async () => {
		setChecking(true);
		await Promise.all([GuildBoosts.loadSlots(), monthly && BoostSubscription.load()]);
		setChecking(false);
		if (GuildBoosts.hasFreeSlot) {
			ToastCommands.createToast({type: 'success', children: i18n._(SLOT_ARRIVED_DESCRIPTOR)});
			return;
		}
		const here = {refresh: GuildBoosts.loadSlots, ready: () => GuildBoosts.hasFreeSlot, boost: handleBoost};
		ModalCommands.push(
			modal(() =>
				monthly ? (
					<BoostSubscriptionModal
						here={{
							...here,
							refresh: () => {
								void GuildBoosts.loadSlots();
								void BoostSubscription.load();
							},
						}}
					/>
				) : (
					<ShopCheckoutModal target={{kind: 'boost', here}} data-flx="boost.buy-button.checkout-modal" />
				),
			),
		);
	};
	return (
		<PurchaseDisabledWrapper disabled={full} tooltipText={i18n._(BOOST_LIMIT_DESCRIPTOR, {count: limit})}>
			<Button
				variant="secondary"
				small
				disabled={full}
				submitting={checking}
				onClick={() => void open()}
				data-flx="boost.buy-button"
			>
				<span className={styles.label}>
					<BoostGemIcon size={16} />
					{i18n._(BUY_BOOST_DESCRIPTOR)}
					<span className={styles.price}>
						{boost.recurring_quote ? (
							formatPayable(boost.recurring_quote.amount_minor, boost.recurring_quote.currency, i18n.locale)
						) : (
							<ShopPrice
								cents={shownCents(boost.price, ShopCatalog.now())}
								currency={priceCurrency(boost.price)}
								primaryOnly
							/>
						)}
					</span>
				</span>
			</Button>
		</PurchaseDisabledWrapper>
	);
});
